import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Play } from 'lucide-react';
import { useState } from 'react';
import { FadeIn } from '@/components';

import XAGImage from '@/assets/brands/xag.webp';
import DJIImage from '@/assets/brands/dji.webp';

// Substitua pelo ID do YouTube do vídeo do curso de mapeamento
const VIDEO_ID = '';

export function MappingCourse() {
  const [isPlaying, setIsPlaying] = useState(false);
  const embedUrl = `https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`;

  return (
    <section id="mapping" className="section-shell bg-muted/40 transition-colors duration-300">
      {/* Grid background */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[24px_24px] opacity-50 dark:opacity-100" />

      {/* Glow */}
      <div className="pointer-events-none absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/8 blur-[100px]" />

      <div className="section-container">
        {/* Top row: text left + video right */}
        <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">
          {/* Text content */}
          <div className="flex flex-col gap-7 lg:flex-1">
            <FadeIn direction="up" distance={20}>
              <p className="mb-1 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
                Curso de Mapeamento 3D
              </p>
              <h2 className="section-title leading-tight">
                Mapeamento Aéreo 3D com{' '}
                <span className="text-primary">Acompanhamento de Relevo</span>
              </h2>
            </FadeIn>

            <FadeIn direction="up" distance={15} delay={0.1}>
              <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                O Guri dos Drones desenvolveu uma metodologia única para criar mapas 3D que
                respeitam fielmente os desníveis do terreno. Garanta aplicações precisas mesmo em
                topografias complexas.
              </p>
            </FadeIn>

            <FadeIn direction="up" distance={15} delay={0.2}>
              <ul className="flex flex-col gap-4">
                {[
                  'Compatível com Drones DJI e XAG',
                  'Maior precisão na aplicação de defensivos',
                  'Economia de insumos e segurança de voo',
                  'Metodologia comprovada em campo',
                ].map((item, i) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-sm text-foreground/70 sm:text-base"
                  >
                    <span className="font-mono text-[10px] tracking-widest text-primary/50">
                      0{i + 1}
                    </span>
                    <span className="h-px w-4 shrink-0 bg-primary/20" />
                    {item}
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>

          {/* Video */}
          <div className="w-full lg:flex-1">
            <FadeIn direction="up" distance={30} delay={0.15}>
              <div className="group relative">
                <div className="absolute -inset-px hidden bg-linear-to-r from-primary/30 via-primary/10 to-primary/30 opacity-20 blur-sm transition duration-1000 group-hover:opacity-40 sm:block" />

                <div className="relative aspect-video overflow-hidden border border-border bg-black shadow-2xl dark:border-white/10">
                  <AnimatePresence mode="wait">
                    {!isPlaying || !VIDEO_ID ? (
                      <motion.div
                        key="thumbnail"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="relative flex h-full w-full items-center justify-center bg-card"
                      >
                        {/* Background grid */}
                        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808010_1px,transparent_1px),linear-gradient(to_bottom,#80808010_1px,transparent_1px)] bg-size-[32px_32px]" />
                        <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />

                        {/* HUD label top-left */}
                        <div className="pointer-events-none absolute top-4 left-4 z-10 flex items-center gap-2 sm:top-5 sm:left-5">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                          </span>
                          <span className="font-mono text-[10px] tracking-widest text-primary/70 uppercase">
                            Mapeamento 3D
                          </span>
                        </div>

                        {/* Play button */}
                        {VIDEO_ID ? (
                          <button
                            type="button"
                            onClick={() => setIsPlaying(true)}
                            className="absolute inset-0 flex items-center justify-center transition-all duration-300"
                          >
                            <div className="relative">
                              <motion.div
                                animate={{ scale: [1, 1.5, 1], opacity: [0.4, 0, 0.4] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="absolute inset-0 rounded-full bg-primary/40 blur-xl"
                              />
                              <div className="relative flex h-16 w-16 items-center justify-center border-2 border-primary/50 bg-primary/20 backdrop-blur-sm shadow-[0_0_50px_rgba(16,185,129,0.3)] transition-transform duration-300 group-hover:scale-110 md:h-24 md:w-24">
                                <Play className="ml-1 h-7 w-7 fill-white text-white md:ml-1.5 md:h-10 md:w-10" />
                              </div>
                            </div>
                          </button>
                        ) : (
                          <div className="relative flex flex-col items-center gap-4 text-center">
                            <div className="flex h-16 w-16 items-center justify-center border border-primary/20 bg-primary/8 md:h-20 md:w-20">
                              <Play className="ml-1 h-7 w-7 text-primary/40 md:h-8 md:w-8" />
                            </div>
                            <p className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground/50 uppercase">
                              Vídeo do curso em breve
                            </p>
                          </div>
                        )}

                        {/* Bottom label */}
                        <div className="absolute right-4 bottom-4 hidden items-center gap-2 border border-white/10 bg-black/60 px-4 py-2 backdrop-blur-md sm:flex sm:right-5 sm:bottom-5">
                          <span className="font-mono text-[10px] font-bold tracking-widest text-white/70 uppercase">
                            Apresentação do Curso
                          </span>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="video"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="h-full w-full"
                      >
                        <iframe
                          src={embedUrl}
                          title="Curso de Mapeamento 3D — Guri dos Drones"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="h-full w-full border-0"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </FadeIn>
          </div>
          {/* end video col */}
        </div>
        {/* end top row */}

        {/* Course cards — full width below */}
        <FadeIn direction="up" distance={20} delay={0.25}>
          <div className="flex flex-col gap-4 mt-3">
            <p className="text-center font-mono text-[10px] tracking-[0.2em] text-muted-foreground/50 uppercase">
              Escolha o curso para o seu drone
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* DJI */}
              <a
                href="https://pay.hotmart.com/C84841784W?bid=1774435603301"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex min-h-72 flex-col justify-between overflow-hidden border border-primary/40 shadow-[0_24px_80px_-42px_rgba(16,185,129,0.4)] transition-all duration-500 hover:border-primary/70 hover:shadow-[0_24px_80px_-32px_rgba(16,185,129,0.6)]"
              >
                <img
                  src={DJIImage}
                  alt="Drone DJI agrícola em campo"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/92 via-black/50 to-black/20" />

                <div className="relative z-10 flex items-center justify-between p-6">
                  <div className="inline-flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-1.5 backdrop-blur-md">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                    </span>
                    <span className="font-mono text-[10px] font-bold tracking-[0.15em] text-white/80 uppercase">
                      Curso DJI
                    </span>
                  </div>
                  <span className="font-mono text-[10px] tracking-widest text-white/30">
                    MAP_01
                  </span>
                </div>

                <div className="relative z-10 p-6">
                  <h3 className="text-balance font-heading text-2xl font-bold leading-tight text-white sm:text-[1.75rem]">
                    Mapeamento para Drones de Pulverização DJI
                  </h3>
                  <p className="mt-3 max-w-[42ch] text-sm leading-relaxed text-white/65 sm:text-base">
                    Fotos manual e automática, SmartFarm Web, Agisoft, QGIS, taxa variável e
                    correção de ângulo. Do voo à missão de pulverização calibrada.
                  </p>
                  <div className="mt-6 inline-flex h-11 items-center gap-2 border border-primary/40 bg-primary/15 px-6 font-mono text-xs font-bold tracking-widest text-primary uppercase transition-all group-hover:border-primary/70 group-hover:bg-primary/25">
                    Acessar Curso
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </a>

              {/* XAG */}
              <a
                href="https://pay.hotmart.com/J95024295G"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex min-h-72 flex-col justify-between overflow-hidden border border-border/50 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.5)] transition-all duration-500 hover:border-primary/50 hover:shadow-[0_24px_80px_-32px_rgba(16,185,129,0.3)]"
              >
                <img
                  src={XAGImage}
                  alt="Drone XAG em operação agrícola"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/92 via-black/50 to-black/20" />

                <div className="relative z-10 flex items-center justify-between p-6">
                  <div className="inline-flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-1.5 backdrop-blur-md">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white/60" />
                    </span>
                    <span className="font-mono text-[10px] font-bold tracking-[0.15em] text-white/80 uppercase">
                      Curso XAG
                    </span>
                  </div>
                  <span className="font-mono text-[10px] tracking-widest text-white/30">
                    MAP_02
                  </span>
                </div>

                <div className="relative z-10 p-6">
                  <h3 className="text-balance font-heading text-2xl font-bold leading-tight text-white sm:text-[1.75rem]">
                    Mapeamento para Drones de Pulverização XAG
                  </h3>
                  <p className="mt-3 max-w-[42ch] text-sm leading-relaxed text-white/65 sm:text-base">
                    RTK, linhas personalizadas, taxa variável por análise de solo, marcação de
                    obstáculos e orto-mosaico no controle. Precisão do mapa à aplicação.
                  </p>
                  <div className="mt-6 inline-flex h-11 items-center gap-2 border border-white/20 bg-white/8 px-6 font-mono text-xs font-bold tracking-widest text-white/80 uppercase backdrop-blur-sm transition-all group-hover:border-white/40 group-hover:bg-white/14">
                    Acessar Curso
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
